import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { PRODUCTS } from '../data/products';
import { submitLead } from '../lib/leads';

// One template for the two Imfuyo Finance pages (insurance and loans).
// Content lives in src/data/products.js. Enquiries go to the admin Enquiries inbox through submitLead.
export default function ProductPage({ product, isDark, onNavigate }) {
  const d = PRODUCTS[product];
  const [form, setForm] = useState(null); // 'apply' | 'claim' | 'loan'
  const formRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0); setForm(null);
    // The Services page can open a form straight away.
    let want = null;
    try { want = sessionStorage.getItem('imfuyo-open-form'); sessionStorage.removeItem('imfuyo-open-form'); } catch (e) { /* ignore */ }
    if (want && ['apply', 'claim', 'loan'].includes(want)) setTimeout(() => { setForm(want); setTimeout(() => formRef.current && formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80); }, 350);
  }, [product]);

  if (!d) return null;

  const bg = isDark ? 'bg-gray-950' : 'bg-white';
  const soft = isDark ? 'bg-gray-900' : 'bg-[#F4F7F2]';
  const head = isDark ? 'text-white' : 'text-[#14301A]';
  const body = isDark ? 'text-gray-300' : 'text-gray-600';
  const card = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const font = { fontFamily: "'Inter', system-ui, sans-serif" };

  const openForm = (kind) => {
    setForm(kind);
    setTimeout(() => formRef.current && formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };
  const cta = (c, i) => {
    const cls = `px-6 py-3 rounded-full font-semibold transition-colors ${i === 0 ? 'bg-[#D55103] hover:bg-[#B84402] text-white' : 'border border-white/50 text-white hover:bg-white/10'}`;
    if (c.action === 'page') return <button key={c.label} onClick={() => onNavigate(c.page)} className={cls}>{c.label}</button>;
    return <button key={c.label} onClick={() => openForm(c.form)} className={cls}>{c.label}</button>;
  };

  return (
    <div className={bg} style={{ ...font, paddingTop: '80px' }}>
      {/* Hero */}
      <section className="bg-[#14301A] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] items-center">
          <div>
            <p className="text-sm font-semibold tracking-widest uppercase text-[#B7D9A8] mb-4">{d.eyebrow}</p>
            <h1 className="text-3xl sm:text-5xl font-bold leading-tight" style={{ textWrap: 'balance' }}>{d.title}</h1>
            <p className="mt-5 text-lg text-white/80 max-w-2xl">{d.sub}</p>
            <div className="mt-8 flex flex-wrap gap-3">{d.ctas.map(cta)}</div>
          </div>
          <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl" style={{ aspectRatio: '4 / 3' }}>
            <img src={d.photo} alt={d.photoAlt} className="w-full h-full object-cover" loading="eager" />
          </div>
        </div>
      </section>

      {/* Facts */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <p className="text-sm font-semibold tracking-widest uppercase text-[#285020] mb-2">What you should know</p>
        <h2 className={`text-2xl sm:text-3xl font-bold mb-8 ${head}`}>Plain facts, no small print.</h2>
        <div className="grid gap-5 md:grid-cols-3">
          {d.facts.map(([t, x]) => (
            <div key={t} className={`rounded-2xl border p-6 ${card}`}>
              <h3 className={`text-lg font-semibold mb-2 ${head}`}>{t}</h3>
              <p className={body}>{x}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Steps */}
      <section className={soft}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <p className="text-sm font-semibold tracking-widest uppercase text-[#285020] mb-2">How it works</p>
          <h2 className={`text-2xl sm:text-3xl font-bold mb-8 ${head}`}>Four steps.</h2>
          <ol className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {d.steps.map((s, i) => (
              <li key={s.title} className={`rounded-2xl border p-6 ${card}`}>
                <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#285020] text-white font-bold mb-4">{i + 1}</span>
                <h3 className={`text-lg font-semibold mb-1 ${head}`}>{s.title}</h3>
                <p className={body}>{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* What you need + who does what */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 grid gap-8 lg:grid-cols-2">
        <div className={`rounded-2xl border p-6 ${card}`}>
          <h3 className={`text-lg font-semibold mb-3 ${head}`}>What you need to start</h3>
          <ul className="space-y-2">
            {d.need.map((n) => (
              <li key={n} className={`flex gap-2 ${body}`}><Check className="w-5 h-5 shrink-0 text-[#285020]" /> <span>{n}</span></li>
            ))}
          </ul>
        </div>
        <div className={`rounded-2xl border-l-4 border-[#500808] p-6 ${isDark ? 'bg-gray-900 text-gray-200' : 'bg-[#FBF3F0] text-gray-800'}`}>
          <strong>Who does what.</strong> {d.who}
        </div>
      </section>

      {/* Enquiry form */}
      <section ref={formRef} className={soft}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <p className="text-sm font-semibold tracking-widest uppercase text-[#285020] mb-2">Get in touch</p>
          <h2 className={`text-2xl sm:text-3xl font-bold mb-2 ${head}`}>{form ? d.formTitles[form] : 'Tell us what you need.'}</h2>
          <p className={`mb-6 ${body}`}>The Imfuyo team replies on WhatsApp or by phone, usually within one working day.</p>
          {!form && (
            <div className="flex flex-wrap gap-3 mb-6">
              {Object.entries(d.formTitles).map(([k, label]) => (
                <button key={k} onClick={() => openForm(k)} className="px-5 py-2.5 rounded-full border border-[#285020] text-[#285020] font-semibold hover:bg-[#285020] hover:text-white transition-colors">{label}</button>
              ))}
            </div>
          )}
          {form && <EnquiryForm kind={form} product={product} label={d.formTitles[form]} isDark={isDark} />}
        </div>
      </section>

      {/* Related */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <p className="text-sm font-semibold tracking-widest uppercase text-[#285020] mb-2">Related</p>
        <h2 className={`text-2xl sm:text-3xl font-bold mb-8 ${head}`}>Learn more before you decide.</h2>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {d.links.map((l) => (
            <button key={l.title} onClick={() => onNavigate(l.page)} className={`group text-left rounded-2xl border p-6 transition-shadow hover:shadow-lg ${card}`}>
              <span className={`block text-lg font-semibold mb-1 ${head}`}>{l.title}</span>
              <span className={`block mb-4 ${body}`}>{l.text}</span>
              <span className="inline-flex items-center gap-1 text-[#285020] font-semibold">Open <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

const LIVESTOCK = ['Dairy goats', 'Meat goats', 'Dairy cows', 'Beef cattle', 'Pigs', 'Camels', 'Mixed'];

function EnquiryForm({ kind, product, label, isDark }) {
  const [v, setV] = useState({ name: '', phone: '', location: '', livestock: 'Dairy goats', count: '', tagged: 'Not yet', detail: '', consent: false });
  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const set = (k) => (e) => setV((o) => ({ ...o, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const inp = `w-full rounded-xl border px-4 py-3 text-[15px] outline-none focus:border-[#285020] ${isDark ? 'bg-gray-950 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const lab = `block text-sm font-semibold mb-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`;
  const detailLabel = kind === 'claim' ? 'What happened, when, and the ear tag number' : kind === 'loan' ? 'What do you want to finance, and roughly how much' : 'Anything else we should know';

  const submit = async (e) => {
    e.preventDefault();
    if (v.name.trim().length < 2 || v.phone.replace(/\D/g, '').length < 9 || !v.consent) { setState('error'); return; }
    setState('sending');
    try {
      await submitLead({
        name: v.name, phone: v.phone, email: '', role: 'Farmer', consent: v.consent,
        subject: `${label} (${product})`,
        source: `website-${product}-${kind}`,
        message: [`${label}.`, `Location: ${v.location || '-'}`, `Livestock: ${v.livestock}, ${v.count || '?'} animals`, `Tagged with Imfuyo: ${v.tagged}`, v.detail ? `Details: ${v.detail}` : ''].filter(Boolean).join('\n'),
        extra: { product, kind, location: v.location, livestock: v.livestock, count: v.count, tagged: v.tagged },
      });
      setState('sent');
    } catch (err) {
      console.error(err);
      setState('error');
    }
  };

  if (state === 'sent') {
    return (
      <div className={`rounded-2xl border p-6 ${isDark ? 'bg-gray-900 border-gray-800 text-gray-100' : 'bg-white border-gray-200 text-gray-800'}`}>
        <p className="text-lg font-semibold mb-1">Thank you, {v.name.trim().split(' ')[0]}.</p>
        <p>We have your {label.toLowerCase()} request and will reach you on {v.phone}. If it is urgent, message us on WhatsApp at +254 110 563 185.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`rounded-2xl border p-6 grid gap-4 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
      <div className="grid gap-4 sm:grid-cols-2">
        <div><label className={lab}>Full name</label><input className={inp} value={v.name} onChange={set('name')} placeholder="Your name" /></div>
        <div><label className={lab}>Phone number</label><input className={inp} value={v.phone} onChange={set('phone')} placeholder="07XX XXX XXX" inputMode="tel" /></div>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div><label className={lab}>County or town</label><input className={inp} value={v.location} onChange={set('location')} placeholder="e.g. Kiambu" /></div>
        <div><label className={lab}>Livestock</label>
          <select className={inp} value={v.livestock} onChange={set('livestock')}>{LIVESTOCK.map((x) => <option key={x}>{x}</option>)}</select></div>
        <div><label className={lab}>How many animals</label><input className={inp} value={v.count} onChange={set('count')} placeholder="e.g. 2" inputMode="numeric" /></div>
      </div>
      <div><label className={lab}>Are the animals tagged with Imfuyo?</label>
        <select className={inp} value={v.tagged} onChange={set('tagged')}>{['Not yet', 'Yes, all of them', 'Some of them'].map((x) => <option key={x}>{x}</option>)}</select></div>
      <div><label className={lab}>{detailLabel}</label><textarea className={inp} rows={4} value={v.detail} onChange={set('detail')} /></div>
      <label className={`flex gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
        <input type="checkbox" checked={v.consent} onChange={set('consent')} className="mt-1" />
        <span>Imfuyo may contact me about this request and may share these details with a partner insurer or lender to answer it. Imfuyo is registered with the Office of the Data Protection Commissioner.</span>
      </label>
      {state === 'error' && <p className="text-sm text-red-600">Please give your name and phone number and tick the box, then try again.</p>}
      <button type="submit" disabled={state === 'sending'} className="justify-self-start px-7 py-3 rounded-full bg-[#D55103] hover:bg-[#B84402] text-white font-semibold disabled:opacity-60">
        {state === 'sending' ? 'Sending…' : `Send ${label.toLowerCase()} request`}
      </button>
    </form>
  );
}
